import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import CCILandingHelmet from 'common/helmets/CCILandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import CCIWordmark from 'img/landing/cci-wordmark.webp';
import Liya from 'img/landing/liya.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const CCILanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed CCI Case Study');
  }, [trackEvent]);

  return (
    <div className="landingCCI landingCustomersPage">
      <CCILandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How CircleCI uses Canny as their home for feedback to inform their product decisions" />
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Liya Ai</div>
              <div className="title">Product Ops Manager at CircleCI</div>
            </div>
            <LazyLoadedImage loading="eager" src={Liya} alt="Liya Ai" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              CircleCI is the leading continuous integration and delivery platform for software
              innovation at scale. With intelligent automation and delivery tools, CircleCI is used
              by the world's best engineering teams to radically reduce the time from idea
              to&nbsp;execution.
            </p>
            <p>
              Since CircleCI is the software delivery “ground floor,” getting first-hand feedback,
              input, and ideas from their customers is a top priority for keeping the platform on
              track and responsive to user&nbsp;needs.
            </p>
            <p>
              Liya Ai is Product Operations Manager at CircleCI. She supports the product team, and
              helps teams optimize their process for growth and better&nbsp;communication.
            </p>
            <p>
              Liya works on making product managers’ lives easier by building repeatable, scalable
              processes, and making sure they understand the voice of the&nbsp;customer.
            </p>
            <p>
              She also interacts with every part of the organization, from revenue, to engineering,
              to marketing, to make sure everyone is on the same&nbsp;page.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              The main problem CircleCI faced was that there were too many complicated places where
              users could give&nbsp;feedback.
            </p>
            <p>
              There were several public channels, as well as a legacy “ideas portal” that was
              implemented&nbsp;internally.
            </p>
            <p className="quote">
              &ldquo;It was a really complex platform, not very user-friendly, and didn’t really
              meet our requirements at the&nbsp;time.&rdquo;
            </p>
            <p>
              It was also difficult to be proactive. Feedback was rolling in from several different
              places at once, and it was a challenge to stay on top of it and be&nbsp;responsive.
            </p>
            <p className="quote">
              &ldquo;We wanted to make it very clear that our product team is listening to
              our&nbsp;users.&rdquo;
            </p>
            <p>
              They also wanted to encourage product managers to be inquisitive, interact with users
              more, and close the feedback&nbsp;loop.
            </p>
            <p>
              So, the team decided they needed a more straightforward, user-friendly&nbsp;solution.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>Before choosing Canny, Liya evaluated several other feedback&nbsp;tools.</p>
            <p className="quote">
              &ldquo;Overall, it came down to Canny’s well-designed UI, and the integrations. Being
              able to integrate with JIRA, Salesforce… Basically everything we&nbsp;needed.&rdquo;
            </p>
            <p>She was also impressed with how easy Canny was to set&nbsp;up.</p>
            <p className="quote">
              &ldquo;The team was really supportive and helpful, and super responsive during
              the&nbsp;process.&rdquo;
            </p>
            <p>
              Currently, all product managers at CircleCI use Canny regularly. They respond to
              customers, and help triage ideas as they come&nbsp;in.
            </p>
            <p>
              The support and engineering teams also occasionally use Canny, in the case of bugs or
              similar&nbsp;issues.
            </p>
            <p className="quote">
              &ldquo;The entire team is working together to try and provide more regular updates to
              our&nbsp;customers.&rdquo;
            </p>
            <p>CircleCI uses Canny to capture feedback in three different&nbsp;ways:</p>
            <ul>
              <li>Capturing internal feedback</li>
              <li>Capturing feedback for features in preview</li>
              <li>Capturing all feedback on a public-facing board</li>
            </ul>
            <p>
              They also use internal comments to communicate between product teams and
              customer-facing&nbsp;teams.
            </p>
            <p>
              Liya is also a fan of the reporting functionality. For her as an admin in Canny, this
              means she can go in and keep track of how things are&nbsp;changing.
            </p>
            <p className="quote">
              &ldquo;Canny has been a really important part of getting an overall picture of
              feedback in one&nbsp;place.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              For CircleCI, Canny’s main benefit has been helping teams make important
              product&nbsp;decisions.
            </p>
            <p className="quote">
              &ldquo;It’s a very valuable data source for us when we’re evaluating and prioritizing
              what we’re working&nbsp;on.&rdquo;
            </p>
            <p>
              Having one source of truth for user feedback has helped standardize their processes,
              by adding the “Canny value” of feature&nbsp;requests.
            </p>
            <p>
              Feedback is now more organized and much easier to navigate for the whole team, since
              it’s all aggregated in one&nbsp;place.
            </p>
            <p className="quote">
              &ldquo;It just makes our lives easier from the product perspective, to be able to go
              to this one tool and see&nbsp;everything.&rdquo;
            </p>
            <p>
              Having product managers go in and interact with customers has also been beneficial to
              creating a connection between&nbsp;them.
            </p>
            <p className="quote">
              &ldquo;[The product managers are] really able to go in and own that experience with
              their users. It's definitely driven a lot of their product&nbsp;decisions.&rdquo;
            </p>
            <p>
              Liya believes customers should be at the heart of everything they do. And, having a
              dedicated customer feedback tool allows users to drive product decisions and have
              a&nbsp;voice.
            </p>
            <p className="quote">
              &ldquo;I would encourage anyone to try out a system where you can make it easier for
              you to communicate with your customers, with the ultimate goal of understanding each
              other&nbsp;better.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage alt="CCI Wordmark" className="CCIWordmark" src={CCIWordmark} />
            Automate your development process with continuous integration in the cloud or on your
            own&nbsp;infrastructure.
            <a href="https://circleci.com/" rel="noopener" target="_blank">
              circleci.com
            </a>
            <div className="stat">
              <UppercaseHeader>Location</UppercaseHeader>
              <p>San Francisco, CA</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2020</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>30,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default hot(CCILanding);
